/**
 * CardInstanceForm - Form for creating/editing collection card instances
 *
 * Used in AddToCollectionModal and EditCollectionCardModal to capture all metadata for a card instance:
 * - Condition, Finish, Language
 * - Folder assignment
 * - Quantity
 * - Purchase info (price, currency, date)
 * - Notes and flags (altered, signed, playtest)
 */
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { IoAdd, IoBookmark, IoRemove } from 'react-icons/io5';

import Dropdown, { type DropdownOption } from '@/components/Dropdown/Dropdown';
import { folderSortModeAtom } from '@/features/folders/atoms';
import { useFoldersQuery } from '@/features/folders/hooks/api';
import { flattenFolders } from '@/features/folders/lib/folderTree';
import { CONDITION_OPTIONS, FINISH_LABELS, LANGUAGE_OPTIONS } from '@/lib/cardUtils';
import { parsePriceInput, sanitizePriceInput } from '@/lib/price';
import { PURCHASE_CURRENCY_OPTIONS } from '@/lib/settingsUtils';
import type { CollectionCard, Condition, Finish, Language } from '@/types/card';

import type { RememberedFieldKey } from '../hooks/useRememberedFields';

// ============================================================================
// Types
// ============================================================================

export interface CardInstanceFormData {
  quantity: number;
  condition: Condition;
  finish: Finish;
  language: Language;
  folderId: string | null;
  purchasePrice: number | null;
  purchaseCurrency: string;
  purchaseDate: string | null;
  notes: string;
  isAltered: boolean;
  isSigned: boolean;
  isPlaytest: boolean;
}

export interface CardInstanceFormProps {
  /** Current form values (controlled component) */
  values: CardInstanceFormData;
  /** Callback when form values change */
  onChange: (data: CardInstanceFormData) => void;
  /** Whether form is disabled (e.g., during submission) */
  disabled?: boolean;
  /** ID prefix for form fields (for label association) */
  idPrefix?: string;
  /** Which fields are currently remembered (shows dot indicator) */
  rememberedFields?: RememberedFieldKey[];
  /** Finishes the printing exists in; others are disabled. null/undefined = unknown, all allowed */
  availableFinishes?: Finish[] | null;
}

// ============================================================================
// Options
// ============================================================================

const FINISH_OPTIONS: { value: Finish; label: string }[] = (Object.entries(FINISH_LABELS) as [Finish, string][]).map(([value, label]) => ({
  value,
  label,
}));

// ============================================================================
// Subcomponents
// ============================================================================

interface QuantityControlProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  id?: string;
}

function QuantityControl({ value, onChange, disabled, id }: QuantityControlProps): ReactElement {
  const decrement = () => onChange(Math.max(1, value - 1));
  const increment = () => onChange(value + 1);

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={decrement}
        disabled={disabled || value <= 1}
        className="bg-slate hover:bg-storm border-storm text-silver hover:text-pearl disabled:text-ash flex h-8 w-8 items-center justify-center rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Decrease quantity"
      >
        <IoRemove className="h-4 w-4" />
      </button>
      <input
        id={id}
        type="number"
        value={value}
        onChange={(e) => {
          const num = parseInt(e.target.value, 10);
          if (!isNaN(num) && num >= 1) onChange(num);
        }}
        min={1}
        disabled={disabled}
        className="bg-slate border-storm text-pearl focus-ring w-16 rounded-lg border px-3 py-1.5 text-center text-sm"
        aria-label="Quantity"
      />
      <button
        type="button"
        onClick={increment}
        disabled={disabled}
        className="bg-slate hover:bg-storm border-storm text-silver hover:text-pearl disabled:text-ash flex h-8 w-8 items-center justify-center rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Increase quantity"
      >
        <IoAdd className="h-4 w-4" />
      </button>
    </div>
  );
}

interface FinishSelectorProps {
  value: Finish;
  onChange: (value: Finish) => void;
  disabled?: boolean;
  idPrefix?: string;
  availableFinishes?: Finish[] | null;
}

function FinishSelector({ value, onChange, disabled, idPrefix = '', availableFinishes }: FinishSelectorProps): ReactElement {
  return (
    <div className="flex gap-2" role="radiogroup" aria-label="Card finish">
      {FINISH_OPTIONS.map((option) => {
        const isUnavailable = availableFinishes ? !availableFinishes.includes(option.value) : false;
        const isDisabled = disabled || isUnavailable;
        return (
          <label
            key={option.value}
            title={isUnavailable ? `This printing was not made in ${option.label.toLowerCase()}` : undefined}
            className={clsx(
              'has-focus-visible:ring-aether flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors has-focus-visible:ring-2',
              value === option.value ? 'bg-aether/20 text-pearl border-aether border' : 'bg-slate text-silver border-storm border',
              isDisabled && 'cursor-not-allowed opacity-50',
              isUnavailable && 'decoration-ash/70 line-through',
            )}
          >
            <input
              type="radio"
              id={`${idPrefix}finish-${option.value}`}
              name={`${idPrefix}finish`}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              disabled={isDisabled}
              className="sr-only"
            />
            {option.label}
          </label>
        );
      })}
    </div>
  );
}

interface PriceInputProps {
  id: string;
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
}

/** Text input accepting `7,35` or `7.35`; keeps the typed text so drafts like `7,` survive. */
function PriceInput({ id, value, onChange, disabled }: PriceInputProps): ReactElement {
  const [text, setText] = useState(value?.toString() ?? '');

  // Sync when the value is replaced from outside (reset, remembered fields)
  useEffect(() => {
    if (parsePriceInput(text) !== value) setText(value?.toString() ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={text}
      onChange={(e) => {
        const next = sanitizePriceInput(e.target.value);
        if (next === null) return;
        setText(next);
        onChange(parsePriceInput(next));
      }}
      placeholder="0.00"
      disabled={disabled}
      className="bg-slate border-storm text-pearl placeholder:text-ash focus-ring h-9 w-full rounded-lg border px-2 text-sm"
    />
  );
}

interface CheckboxFieldProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  remembered?: boolean;
}

function CheckboxField({ id, label, checked, onChange, disabled, remembered }: CheckboxFieldProps): ReactElement {
  return (
    <label className={clsx('flex cursor-pointer items-center gap-2 text-sm', disabled && 'cursor-not-allowed opacity-50')}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="accent-aether h-4 w-4 rounded"
      />
      <span className="text-silver">
        {label}
        {remembered && <IoBookmark className="ml-1 inline-block h-3 w-3 align-middle text-amber-400" />}
      </span>
    </label>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function CardInstanceForm({
  values: formData,
  onChange,
  disabled = false,
  idPrefix = 'instance-',
  rememberedFields,
  availableFinishes,
}: CardInstanceFormProps): ReactElement {
  const { data: folders } = useFoldersQuery();
  const folderSortMode = useAtomValue(folderSortModeAtom);

  const isFieldRemembered = (key: RememberedFieldKey) => rememberedFields?.includes(key) ?? false;

  /** Small bookmark icon shown after a label when the field is remembered. */
  const dot = (key: RememberedFieldKey) =>
    isFieldRemembered(key) ? <IoBookmark className="ml-1 inline-block h-3 w-3 align-middle text-amber-400" /> : null;

  // Update a single field and notify parent
  const updateField = <K extends keyof CardInstanceFormData>(field: K, value: CardInstanceFormData[K]) => {
    onChange({ ...formData, [field]: value });
  };

  // Build folder options — ordered and indented to match the inventory sidebar.
  const folderOptions: DropdownOption<string>[] = useMemo(
    () =>
      flattenFolders(folders ?? [], folderSortMode).map(({ folder, depth }) => ({
        value: folder.id,
        label: `${'\u00A0\u00A0'.repeat(depth)}${folder.name}`,
      })),
    [folders, folderSortMode],
  );

  return (
    <div className="space-y-3">
      {/* Row 1: Quantity & Finish */}
      <div className="flex items-end gap-4">
        <div className="shrink-0">
          <label htmlFor={`${idPrefix}quantity`} className="text-silver mb-1.5 block text-sm font-medium">
            Quantity
          </label>
          <QuantityControl
            id={`${idPrefix}quantity`}
            value={formData.quantity}
            onChange={(v) => updateField('quantity', v)}
            disabled={disabled}
          />
        </div>
        <div>
          <label className="text-silver mb-1.5 block text-sm font-medium">Finish{dot('finish')}</label>
          <FinishSelector
            value={formData.finish}
            onChange={(v) => updateField('finish', v)}
            disabled={disabled}
            idPrefix={idPrefix}
            availableFinishes={availableFinishes}
          />
        </div>
      </div>

      {/* Row 2: Condition & Language */}
      <div className="flex items-end gap-4">
        <div className="w-36">
          <label htmlFor={`${idPrefix}condition`} className="text-silver mb-1.5 block text-sm font-medium">
            Condition{dot('condition')}
          </label>
          <Dropdown
            id={`${idPrefix}condition`}
            value={formData.condition}
            options={CONDITION_OPTIONS}
            onChange={(v) => updateField('condition', v ?? 'NM')}
            disabled={disabled}
            aria-label="Card condition"
          />
        </div>
        <div className="w-28">
          <label htmlFor={`${idPrefix}language`} className="text-silver mb-1.5 block text-sm font-medium">
            Language{dot('language')}
          </label>
          <Dropdown
            id={`${idPrefix}language`}
            value={formData.language}
            options={LANGUAGE_OPTIONS}
            onChange={(v) => updateField('language', v ?? 'en')}
            disabled={disabled}
            aria-label="Card language"
          />
        </div>
      </div>

      {/* Card Flags (Altered, Signed, Playtest) */}
      <div className="flex flex-wrap gap-4">
        <CheckboxField
          id={`${idPrefix}altered`}
          label="Altered"
          checked={formData.isAltered}
          onChange={(v) => updateField('isAltered', v)}
          disabled={disabled}
          remembered={isFieldRemembered('isAltered')}
        />
        <CheckboxField
          id={`${idPrefix}signed`}
          label="Signed"
          checked={formData.isSigned}
          onChange={(v) => updateField('isSigned', v)}
          disabled={disabled}
          remembered={isFieldRemembered('isSigned')}
        />
        <CheckboxField
          id={`${idPrefix}playtest`}
          label="Playtest"
          checked={formData.isPlaytest}
          onChange={(v) => updateField('isPlaytest', v)}
          disabled={disabled}
          remembered={isFieldRemembered('isPlaytest')}
        />
      </div>

      {/* Folder */}
      <div>
        <label htmlFor={`${idPrefix}folder`} className="text-silver mb-1.5 block text-sm font-medium">
          Folder{dot('folderId')}
        </label>
        <Dropdown
          id={`${idPrefix}folder`}
          value={formData.folderId}
          options={folderOptions}
          onChange={(v) => updateField('folderId', v)}
          placeholder="Unfiled"
          allowEmpty
          disabled={disabled}
          aria-label="Folder"
        />
      </div>

      {/* Purchase Info Section - All on one row */}
      <div className="border-storm space-y-2 border-t pt-3">
        <h4 className="text-ash text-xs font-medium tracking-widest uppercase">Purchase Info</h4>

        <div className="flex items-end gap-2">
          <div className="w-36 shrink-0">
            <label htmlFor={`${idPrefix}date`} className="text-silver mb-1.5 block text-sm font-medium">
              Date{dot('purchaseDate')}
            </label>
            <input
              id={`${idPrefix}date`}
              type="date"
              value={formData.purchaseDate ?? ''}
              onChange={(e) => updateField('purchaseDate', e.target.value || null)}
              disabled={disabled}
              className="bg-slate border-storm text-pearl focus-ring h-9 w-full rounded-lg border px-2 text-sm"
            />
          </div>
          <div className="w-20">
            <label htmlFor={`${idPrefix}price`} className="text-silver mb-1.5 block text-sm font-medium">
              Price{dot('purchasePrice')}
            </label>
            <PriceInput
              id={`${idPrefix}price`}
              value={formData.purchasePrice}
              onChange={(v) => updateField('purchasePrice', v)}
              disabled={disabled}
            />
          </div>
          <div className="w-20">
            <label htmlFor={`${idPrefix}currency`} className="text-silver mb-1.5 block text-sm font-medium whitespace-nowrap">
              Currency{dot('purchaseCurrency')}
            </label>
            <Dropdown
              id={`${idPrefix}currency`}
              value={formData.purchaseCurrency}
              options={PURCHASE_CURRENCY_OPTIONS}
              onChange={(v) => updateField('purchaseCurrency', v ?? 'USD')}
              disabled={disabled}
              aria-label="Currency"
              className="h-9"
            />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label htmlFor={`${idPrefix}notes`} className="text-silver mb-1.5 block text-sm font-medium">
          Notes{dot('notes')}
        </label>
        <textarea
          id={`${idPrefix}notes`}
          value={formData.notes}
          onChange={(e) => updateField('notes', e.target.value)}
          placeholder="Add notes about this card..."
          rows={2}
          disabled={disabled}
          className="bg-slate border-storm text-pearl placeholder:text-ash focus-ring w-full resize-none rounded-lg border px-3 py-2 text-sm"
        />
      </div>
    </div>
  );
}

/** Form values prefilled from an existing collection instance */
export function collectionCardToFormData(card: CollectionCard, defaultCurrency: string): CardInstanceFormData {
  return {
    quantity: card.quantity,
    condition: card.condition,
    finish: card.finish,
    language: card.language,
    folderId: card.folder_id,
    purchasePrice: card.purchase_price,
    purchaseCurrency: card.purchase_currency ?? defaultCurrency,
    purchaseDate: card.purchase_date,
    notes: card.notes ?? '',
    isAltered: card.is_altered,
    isSigned: card.is_signed,
    isPlaytest: card.is_playtest,
  };
}

/** Instance fields in the collection document shape (shared by create and update) */
export function formDataToUpdateCard(scryfallId: string, formData: CardInstanceFormData) {
  return {
    scryfall_id: scryfallId,
    quantity: formData.quantity,
    condition: formData.condition,
    finish: formData.finish,
    language: formData.language,
    folder_id: formData.folderId,
    purchase_price: formData.purchasePrice,
    purchase_currency: formData.purchasePrice != null ? formData.purchaseCurrency : null,
    purchase_date: formData.purchaseDate,
    notes: formData.notes.trim() || null,
    is_altered: formData.isAltered,
    is_signed: formData.isSigned,
    is_playtest: formData.isPlaytest,
  };
}

/**
 * Get form data in the format needed for CreateCollectionCard
 */
export function formDataToCreateCard(
  scryfallId: string,
  formData: CardInstanceFormData,
): ReturnType<typeof formDataToUpdateCard> & { source_system: 'manual' } {
  return { ...formDataToUpdateCard(scryfallId, formData), source_system: 'manual' };
}
